import React, { useState, useEffect, useRef } from "react";
import { QueryBuilder, RuleGroupType, formatQuery, defaultControlElements, Controls, Field, ValueEditorProps, OperatorSelectorProps, RuleType, FullField, generateID } from "react-querybuilder";
import "../css/others/FilterPage.css";
import "../css/others/Table.css";
import { SQLQueryGeneratorProps } from "../interFaces/interfaces";
import { apiCall } from "../main/ApiCall";
const BuildFormulaquerygenerater: React.FC<SQLQueryGeneratorProps> = ({ attributes, onQueryChange, query: parentQuery, disabled = false, dateDropdownValues = [15, 30, 45] }) => {
  const [query, setQuery] = useState<RuleGroupType>(parentQuery);
  const [isDefaultFilters, setIsDefaultFilters] = useState(true);
  const [activeInput, setActiveInput] = useState<string | null>(null);
  const [projectCategories, setProjectCategories] = useState<string[]>([]);
  const [billingTypes, setBillingTypes] = useState<string[]>([]);
  const [targetMLs, setTargetMLs] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const excludedFields = ["Target ML", "Billing Type"];
  const clientId = localStorage.getItem("clientId");
  useEffect(() => {
    const fetchDropdownValues = async () => {
      if (!clientId) return;
      setLoading(true);
      try {
        const response = await apiCall("get", `/api/projects/dropdown-filter?clientId=${clientId}`);
        const filters = response?.filters ?? {};
        setProjectCategories(filters.projectCategory ?? []);
        setBillingTypes(filters.billingType ?? []);
        setTargetMLs(filters.targetML ?? []);
      } catch (error) {
        console.error("Failed to fetch dropdown values:", error);
        setProjectCategories([]);
        setBillingTypes([]);
        setTargetMLs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDropdownValues();
  }, [clientId]);
  const fields: Field[] = attributes.map((attr) => ({
    name: attr.name, label: attr.label, inputType: attr.type === "date" ? "date" : "text",
    type: attr.label === "Team Size" ? "number" : attr.type,
    operators: [{ name: "=", label: "=" }, { name: "<", label: "<" }, { name: ">", label: ">" }, { name: "<=", label: "<=" }, { name: ">=", label: ">=" }, { name: "!=", label: "!=" }, { name: "in", label: "in" }],
    values: attr.label === "Project Category" ? projectCategories as any : attr.label === "Billing Type" ? billingTypes as any : attr.label === "Target ML" ? targetMLs as any : undefined
  }));
  const defaultFields = fields.filter((field) => !excludedFields.includes(field.label));
  useEffect(() => {
    setQuery(parentQuery);
    const isDefault =
      parentQuery.rules.length === 0 ||
      (parentQuery.rules.length === defaultFields.length &&
        parentQuery.rules.every(
          (rule) => "field" in rule && defaultFields.some((f) => f.name === rule.field)
        ));
    setIsDefaultFilters(isDefault);
  }, [parentQuery, defaultFields]);
  const handleQueryChange = (newQuery: RuleGroupType) => {
    if (disabled) return;
    const usedFields = new Set<string>();
    const updatedRules = newQuery.rules
      .map((rule) => {
        if ("field" in rule) {
          let updatedRule = { ...rule };
          const fieldLabel = attributes.find(a => a.name === rule.field)?.label;
          if (fieldLabel === "Project Start Date" || fieldLabel === "Project End Date") {
            updatedRule.operator = ">";
          }
          if (fieldLabel === "Project Category" || fieldLabel === "Billing Type" || fieldLabel === "Target ML") {
            if (!["=", "in"].includes(updatedRule.operator)) {
              updatedRule.operator = "=";
            }
          }
          if (fieldLabel === "Team Size") {
            if (!["=", "<", ">", "<=", ">="].includes(updatedRule.operator)) {
              updatedRule.operator = "=";
            }
          }
          if (usedFields.has(updatedRule.field)) {
            const nextAvailable = attributes.find((f) => !usedFields.has(f.name));
            if (nextAvailable) {
              usedFields.add(nextAvailable.name);
              updatedRule.field = nextAvailable.name;
            } else {
              return null;
            }
          }
          usedFields.add(updatedRule.field);
          return updatedRule;
        }
        return rule;
      })
      .filter((rule): rule is RuleType => rule !== null);
    const updatedQuery = { ...newQuery, rules: updatedRules };
    setQuery(updatedQuery);
    onQueryChange(updatedQuery);
  };
  const usedFields = new Set<string>();
  query.rules.forEach((rule) => {
    if ("field" in rule) {
      usedFields.add(rule.field);
    }
  });
  const CustomFieldSelector = ({ options, value, handleOnChange }: any) => {
    const availableOptions = isDefaultFilters
      ? options.filter(
        (opt: any) =>
          !defaultFields.some((f) => f.name === opt.name) || opt.name === value
      )
      : options.filter(
        (opt: any) => !usedFields.has(opt.name) || opt.name === value
      );
    return (
      <select className="custom-field-selector" value={value} onChange={(e) => handleOnChange(e.target.value)} disabled={disabled || loading}>
        {availableOptions.map((opt: any) => (
          <option key={opt.name} value={opt.name}>{opt.label}</option>
        ))}
      </select>
    );
  };
  const handleInputFocus = (fieldName: unknown) => {
    if (typeof fieldName === "string") {
      setActiveInput(fieldName);
    }
  };
  const MultiSelectEditor: React.FC<{ options: string[]; value: string[] | string; onChange: (value: string[] | string) => void; disabled?: boolean; placeholder?: string }> = ({ options, value, onChange, disabled = false, placeholder = "Select options" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const selectedValues = Array.isArray(value) ? value : (value ? value.toString().split(',').map(v => v.trim()).filter(v => v !== '') : []);
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const toggleOption = (option: string) => {
      const newSelection = selectedValues.includes(option) ? selectedValues.filter(v => v !== option) : [...selectedValues, option];
      onChange(newSelection);
    };
    const removeOption = (optionToRemove: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const newSelection = selectedValues.filter(v => v !== optionToRemove);
      onChange(newSelection);
    };
    return (
      <div className="multi-select-container" ref={containerRef}>
        <div className={`multi-select-display ${disabled ? 'disabled' : ''}`} onClick={() => !disabled && setIsOpen(!isOpen)}>
          {selectedValues.length > 0 ? (
            selectedValues.map(val => (
              <span key={val} className="selected-tag">{val}
                {!disabled && (
                  <span className="remove-tag" onClick={(e) => removeOption(val, e)}>×</span>
                )}
              </span>
            ))
          ) : (
            <span className="placeholder-text">{placeholder}</span>
          )}
        </div>
        {isOpen && !disabled && (
          <div className="multi-select-dropdown">
            {options.length > 0 ? (
              options.map(option => (
                <div key={option} className={`dropdown-option ${selectedValues.includes(option) ? 'selected' : ''}`} onClick={() => toggleOption(option)}>
                  <input type="checkbox" checked={selectedValues.includes(option)} onChange={() => { }} className="dropdown-checkbox" />
                  <span className="option-label">{option}</span>
                </div>
              ))
            ) : (
              <div className="no-options">No options available</div>
            )}
          </div>
        )}
      </div>
    );
  };
  const SingleSelectEditor: React.FC<{ options: string[]; value: string; onChange: (value: string) => void; disabled?: boolean; placeholder?: string }> = ({ options, value, onChange, disabled = false, placeholder = "Select an option" }) => {
    return (
      <select className="single-select-dropdown" value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        <option value="">{placeholder}</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  };
  const CustomValueEditor: React.FC<ValueEditorProps> = ({ handleOnChange, value, field, operator }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const selectRef = useRef<HTMLSelectElement>(null);
    const fieldDef = fields.find((f) => f.name === field);
    const fieldType = fieldDef?.inputType;
    const fieldLabel = fieldDef?.label;
    const isTeamSize = fieldLabel === "Team Size";
    const dropdownOptions = fieldDef?.values as string[] | undefined || [];
    useEffect(() => {
      if (activeInput === field) {
        if (fieldType === "date" && selectRef.current) {
          selectRef.current.focus({ preventScroll: true });
        } else if (inputRef.current) {
          inputRef.current.focus({ preventScroll: true });
        }
      }
    }, [field, fieldType]);
    if (["Project Category", "Billing Type", "Target ML"].includes(fieldLabel || "")) {
      if (operator === "in") {
        return (
          <MultiSelectEditor options={dropdownOptions} value={value} onChange={(newValue) => handleOnChange(newValue)} disabled={disabled} placeholder={`Select ${fieldLabel}(s)`} />
        );
      }
      return (
        <SingleSelectEditor options={dropdownOptions} value={typeof value === 'string' ? value : ''} onChange={(newValue) => handleOnChange(newValue)} disabled={disabled} placeholder={`Select a ${fieldLabel}`} />
      );
    }
    if (fieldType === "date") {
      return (
        <select value={value || ""} onChange={(e) => handleOnChange(Number(e.target.value))} disabled={disabled} onFocus={() => handleInputFocus(field)} ref={selectRef} className="custom-value-editor-select">
          <option value="">Select value</option>
          {dateDropdownValues.map(opt => (<option key={opt} value={opt}>{opt}</option>))}
        </select>
      );
    }
    if (isTeamSize) {
      return (
        <input ref={inputRef} type="text" value={value || ""} onChange={(e) => {
          if (disabled) return;
          const newValue = e.target.value;
          if (newValue !== "" && !/^-?\d+$/.test(newValue)) {
            return;
          }
          handleOnChange(newValue)
        }}
          onKeyPress={(e) => { if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Tab") { e.preventDefault() } }} placeholder="Enter numbers only" onFocus={() => handleInputFocus(field)} disabled={disabled} className="team-size-input" />
      );
    }
    if (fieldDef?.type === "number") {
      return (
        <input className="custom-value-editor-input" ref={inputRef} type="number" value={value || ""} placeholder="Enter a number" onChange={(e) => { if (disabled) return; handleOnChange(e.target.value); }} onFocus={() => handleInputFocus(field)} disabled={disabled} />
      );
    }
    const placeholder = ["Billing Type", "Target ML", "Project Category"].includes(fieldLabel || "") ? operator === "in" ? "Enter multiple values separated by commas" : "Enter a single value" : "Enter a value";
    return (
      <input className="custom-value-editor-input" ref={inputRef} type="text" value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={(e) => {
        if (disabled) return;
        if (operator === "in") {
          const values = e.target.value.split(",").map((v) => v.trim()).filter(v => v !== "");
          handleOnChange(values);
        } else {
          handleOnChange(e.target.value);
        }
      }}
        placeholder={placeholder} onFocus={() => handleInputFocus(field)} disabled={disabled}
      />
    );
  };
  const CustomOperatorSelector: React.FC<OperatorSelectorProps & { field: string }> = ({ handleOnChange, value, field }) => {
    const label = fields.find((f) => f.name === field)?.label;
    let ops: string[] = ["="];
    if (["Project Start Date", "Project End Date"].includes(label || "")) {
      ops = [">"];
    } else if (["Project Category", "Billing Type", "Target ML"].includes(label || "")) {
      ops = ["=", "in"];
    } else if (label === "Team Size") {
      ops = ["=", "<", ">", "<=", ">="];
    }
    return (
      <select className="custom-operator-selector" value={value} onChange={(e) => handleOnChange(e.target.value)} disabled={disabled} >
        {ops.map((op) => (
          <option key={op} value={op}>{op}</option>
        ))}
      </select>
    );
  };
  const customControlElements: Controls<FullField, string> = {
    ...defaultControlElements,
    fieldSelector: CustomFieldSelector,
    valueEditor: CustomValueEditor,
    operatorSelector: (props) => (
      <CustomOperatorSelector {...props} field={props.rule.field} />
    ),
    combinatorSelector: () => null,
    addGroupAction: () => null,
  };
  return (
    <div>
      <div className="display-screen">
        <p>
          {query.rules.length === 0 ? "No Filter Applied" : formatQuery(query, { format: "sql", fields })}
        </p>
      </div>
      <div className={`custom-query-builder ${disabled ? "disabled" : ""}`}>
        <QueryBuilder fields={fields} query={query.rules.length === 0 ? { ...query, rules: defaultFields.map((field) => ({ id: generateID(), field: field.name, operator: field.inputType === "date" ? ">" : "=", value: "" })) } : query} onQueryChange={handleQueryChange} controlElements={customControlElements} controlClassnames={{ queryBuilder: "custom-query-builder", ruleGroup: "custom-rule-group", rule: "custom-rule", fields: "custom-fields-selector", operators: "custom-operators-selector", value: "custom-value-editor", removeRule: "custom-remove-rule", addRule: "custom-add-rule" }} />
      </div>
    </div>
  );
};
export default BuildFormulaquerygenerater;